import React, {useState, useEffect, useCallback} from "react";
import {
    Table, Modal, Typography, Descriptions, Button, Space, Col, Select, Row, Input, Form, message
} from "antd";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import theme from "../theme";
import promoterService from "../services/PromoterService";
import PromoterRegisterForm from "../components/promoters/PromoterRegisterForm";
import {COT_ROLE, DISTRICT_ROLE, PROMOTER_ROLE, SECTIONAL_ROLE, useUserRole} from "../utils/userSessionUtils";
import {useNavigate} from "react-router-dom";
import formatDate from "../utils/dateUtils";
import {CreditCardOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, PlusOutlined, SearchOutlined, UndoOutlined} from "@ant-design/icons";
import {jsPDF} from "jspdf";
import autoTable from "jspdf-autotable";
import peopleService from "../services/PeopleService";
import {useSelector} from "react-redux";
import goalService from "../services/GoalService";
import {showSectionSelectorModal} from "../components/promoters/SectionSelectorModal";
import {getConnectionStatus} from "../services/socketService";
import RejectedPromotersModal from "../components/promoters/RejectedPromotersModal";
import {temporaryEmailRule} from "../utils/emailValidator";

const {Title} = Typography;

const PromotersPage = () => {
    const [promoters, setPromoters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPromoter, setSelectedPromoter] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isFormModalVisible, setIsFormModalVisible] = useState(false);
    const userRole = useUserRole();
    const isMobile = useMediaQuery({maxWidth: 800});
    const navigate = useNavigate();
    const auth = useSelector((state) => state.auth || {});
    const [userEntityId] = useState(auth.user?.entityId || []);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});
    const [modal, contextHolder] = Modal.useModal();
    const [selectedSection, setSelectedSection] = useState(null);
    const user = auth.user || {};
    const {entityId, route: userSections, group: userGroup} = user;
    const canDownload = [COT_ROLE, DISTRICT_ROLE].includes(userRole) && userGroup?.toLowerCase() === "estructura";
    const [connectionStatus, setConnectionStatus] = useState(getConnectionStatus());
    const [showRejectedModal, setShowRejectedModal] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [promoterToDelete, setPromoterToDelete] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deletionReason, setDeletionReason] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [emailModalVisible, setEmailModalVisible] = useState(false);
    const [promoterToEditEmail, setPromoterToEditEmail] = useState(null);
    const [emailForm] = Form.useForm();
    const [emailLoading, setEmailLoading] = useState(false);


    useEffect(() => {
        const interval = setInterval(() => {
            setConnectionStatus(getConnectionStatus());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const [tempFilters, setTempFilters] = useState({
        sectionId: [],
    });

    const [filters, setFilters] = useState({
        sectionId: {value: userSections, operator: "in"},
        entityId: {value: entityId, operator: "eq"},
        status: {value: "ACTIVE", operator: "eq"},
    });

    const fetchPromoters = useCallback(async (appliedFilters, page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const response = await promoterService.getAllWithPagination({
                ...appliedFilters
            }, page, pageSize);
            setPromoters(response.data);
            setPagination({
                current: response.meta.page,
                pageSize: response.meta.pageSize,
                total: response.meta.totalItems,
            });
        } catch (error) {
            console.error("Error fetching promoters:", error);
        } finally {
            setLoading(false);
        }
    }, [setPromoters, setLoading, setPagination]);


    useEffect(() => {
        if (!entityId || !userSections?.length) return;
        const initialFilters = {
            sectionId: {value: userSections, operator: "in"},
            entityId: {value: entityId, operator: "eq"},
            status: {value: "ACTIVE", operator: "eq"},
        };
        fetchPromoters(initialFilters, 1, 10).catch(console.error);
    }, [userSections, fetchPromoters, entityId]);

    const handleRowClick = (record) => {
        setSelectedPromoter(record);
        setIsModalVisible(true);
    };

    const handleFilterChange = (key, value) => {
        setTempFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const applyFilters = () => {
        const newFilters = {
            sectionId: {value: tempFilters.sectionId, operator: "in"},
            entityId: {value: entityId, operator: "eq"},
            status: {value: "ACTIVE", operator: "eq"},
        };
        setFilters(newFilters);
        fetchPromoters(newFilters, 1, 10).catch(console.error);
    };

    const resetFilters = () => {
        const resetTempFilters = {sectionId: []};
        const resetAppliedFilters = {
            sectionId: {value: userSections, operator: "in"},
            entityId: {value: entityId, operator: "eq"},
            status: {value: "ACTIVE", operator: "eq"},
        };
        setTempFilters(resetTempFilters);
        setFilters(resetAppliedFilters);
        fetchPromoters(resetAppliedFilters, 1, 10).catch(console.error);
    };


    const downloadPromoterPeople = async (record, e) => {
        e.stopPropagation();
        const fullName = `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`.trim();
        message.loading({content: "Generando PDF...", key: "pdf"});
        try {
            const response = await peopleService.getAll(1, 10000, {
                createdBy: {value: record.electorKey, operator: "eq"},
            });
            const allPeople = response.data || [];

            if (!allPeople.length) {
                message.warning({content: "No hay convencidos para este promotor", key: "pdf"});
                return;
            }

            const doc = new jsPDF({orientation: "landscape"});
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const title = `C ${fullName}`;
            const dateStr = new Date().toLocaleString("es-MX", {dateStyle: "long", timeStyle: "short"});
            const addressParts = [
                record.street && `${record.street}${record.exteriorNumber ? ` ${record.exteriorNumber}` : ""}${record.interiorNumber ? ` Int. ${record.interiorNumber}` : ""}`,
                record.neighborhood,
                record.zipCode && `C.P. ${record.zipCode}`,
            ].filter(Boolean);
            const addressStr = addressParts.join(", ");
            const phoneStr = record.phone ? `Tel. ${record.phone}` : "";

            const headerLines = [addressStr, phoneStr].filter(Boolean).length;

            const drawHeader = () => {
                doc.setFontSize(12);
                doc.setTextColor(80, 80, 80);
                doc.text(title, pageWidth / 2, 14, {align: "center"});
                let nextY = 20;
                if (addressStr) {
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.text(addressStr, pageWidth / 2, nextY, {align: "center"});
                    nextY += 6;
                }
                if (phoneStr) {
                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.text(phoneStr, pageWidth / 2, nextY, {align: "center"});
                    nextY += 6;
                }
                doc.setFontSize(9);
                doc.setTextColor(120, 120, 120);
                doc.text(`Generado: ${dateStr}`, pageWidth / 2, nextY, {align: "center"});
            };

            drawHeader();

            const rows = allPeople.map((p) => [
                `${p.name || ""} ${p.firstLastName || ""} ${p.secondLastName || ""}`.trim(),
                p.phone || "",
                [
                    `${p.street || ""}${p.exteriorNumber ? ` ${p.exteriorNumber}` : ""}${p.interiorNumber ? ` Int. ${p.interiorNumber}` : ""}`,
                    p.neighborhood,
                    p.zipCode ? `CP ${p.zipCode}` : "",
                ].filter(Boolean).join(", "),
                p.sectionId || "",
            ]);

            autoTable(doc, {
                head: [["Nombre", "Teléfono", "Dirección", "Sección"]],
                body: rows,
                startY: 20 + headerLines * 6 + 6,
                margin: {top: 20 + headerLines * 6 + 10},
                headStyles: {fillColor: [100, 100, 100], textColor: 255, fontStyle: "bold"},
                alternateRowStyles: {fillColor: [240, 240, 240]},
                styles: {fontSize: 7, cellPadding: 2, textColor: [60, 60, 60]},
                didDrawPage: (data) => {
                    if (data.pageNumber > 1) drawHeader();
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Página ${data.pageNumber}`, pageWidth / 2, pageHeight - 8, {align: "center"});
                },
            });

            doc.save(`convencidos_${record.electorKey}_${new Date().toISOString().slice(0, 10)}.pdf`);
            message.success({content: "PDF generado correctamente", key: "pdf"});
        } catch (error) {
            console.error("downloadPromoterPeople error:", error);
            message.error({content: "Error al generar PDF", key: "pdf"});
        }
    };

    const desktopColumns = [
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => record.name + " " + record.firstLastName + " " + (record.secondLastName || '')
        },
        {title: "Clave de elector", dataIndex: "electorKey", key: "electorKey"},
        {
            title: "Sección",
            key: "section",
            render: (record) => record.sections ? record.sections.join(", ") : record.sectionId
        },
        {title: "Total actual", dataIndex: "count", key: "count"},
        {
            title: "Acciones",
            key: "actions",
            render: (record) => (
                <Space size="middle">
                    <Button
                        type="default"
                        icon={<EyeOutlined/>}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/people-as-user/${record.electorKey}/PROMOTERS`);
                        }}
                    >
                        Ver Registros
                    </Button>
                    {canDownload && (
                        <Button
                            type="default"
                            icon={<FilePdfOutlined/>}
                            size="small"
                            onClick={(e) => downloadPromoterPeople(record, e)}
                        >
                            Descargar Convencidos
                        </Button>
                    )}
                    {((record.roles && record.roles.includes(SECTIONAL_ROLE)) && [COT_ROLE, DISTRICT_ROLE].includes(userRole)) && (
                        <Button
                            type="default"
                            icon={<CreditCardOutlined/>}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/informacion-nomina/${record.electorKey}`);
                            }}
                        >
                            Ver información de nómina
                        </Button>
                    )}
                    {userRole === DISTRICT_ROLE && (
                        <>
                            <Button
                                type="default"
                                icon={<EditOutlined/>}
                                size="small"
                                onClick={(e) => handleEditEmailClick(record, e)}
                            >
                                Editar correo
                            </Button>
                            <Button
                                danger
                                icon={<DeleteOutlined/>}
                                size="small"
                                onClick={(e) => handleDeleteClick(record, e)}
                            >
                                Dar de baja
                            </Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const mobileColumns = [
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => record.name + " " + record.firstLastName,
        },
        {title: "Registros", dataIndex: "count", key: "count"},
        {
            title: "",
            key: "actions",
            render: (record) => (
                <Space size="middle">
                    <Button
                        type="default"
                        icon={<EyeOutlined/>}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/people-as-user/${record.electorKey}/PROMOTERS`);
                        }}
                    />
                    {canDownload && (
                        <Button
                            type="default"
                            icon={<FilePdfOutlined/>}
                            size="small"
                            onClick={(e) => downloadPromoterPeople(record, e)}
                        />
                    )}
                    {((record.roles && record.roles.includes(SECTIONAL_ROLE)) && [COT_ROLE, DISTRICT_ROLE].includes(userRole)) && (
                        <Button
                            type="default"
                            icon={<CreditCardOutlined/>}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/informacion-nomina/${record.electorKey}`);
                            }}
                        />
                    )}
                    {userRole === DISTRICT_ROLE && (
                        <>
                            <Button
                                type="default"
                                icon={<EditOutlined/>}
                                size="small"
                                onClick={(e) => handleEditEmailClick(record, e)}
                            />
                            <Button
                                danger
                                icon={<DeleteOutlined/>}
                                size="small"
                                onClick={(e) => handleDeleteClick(record, e)}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const handleNewPromoter = async () => {
        try {
            if (userSections.length === 1) {
                const filters = {
                    entityId: {value: userEntityId, operator: "eq"},
                    sectionId: {value: userSections, operator: "in"},
                };

                if (connectionStatus) {
                    // const {data: goals} = await goalService.getAll(filters);
                    // const {meta: metaPromoters} = await promoterService.getAll(filters);

                    // if (goals.length === 1 && goals[0].promotersLimit > metaPromoters.totalItems) {
                        setSelectedSection(userSections[0]);
                        setIsFormModalVisible(true);
                    // } else {
                    //     modal.warning({
                    //         title: 'Límite de promotores',
                    //         content: 'No está permitido agregar más promotores',
                    //     });
                    // }
                } else {
                    setSelectedSection(userSections[0]);
                    setIsFormModalVisible(true);
                }
            } else {
                const selectedSectionId = await showSectionSelectorModal({
                    sections: userSections,
                    entityId: userEntityId,
                    goalService,
                    promoterService
                });

                if (selectedSectionId) {
                    setSelectedSection(selectedSectionId);
                    setIsFormModalVisible(true);
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleTableChange = (paginationInfo) => {
        setPagination((prev) => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
        }));
        fetchPromoters(filters, paginationInfo.current, paginationInfo.pageSize).catch(console.error);
    };

    const handleDeleteClick = (record, e) => {
        e.stopPropagation();
        setPromoterToDelete(record);
        setDeleteConfirmText('');
        setDeletionReason('');
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirmText !== promoterToDelete.electorKey) {
            modal.error({
                title: 'Error',
                content: 'La clave de elector no coincide',
            });
            return;
        }

        if (!deletionReason || deletionReason.trim() === '') {
            modal.error({
                title: 'Error',
                content: 'El motivo de eliminación es obligatorio',
            });
            return;
        }

        setDeleteLoading(true);
        try {
            await promoterService.delete(promoterToDelete.id, deletionReason);
            modal.success({
                title: 'Éxito',
                content: 'Promotor dado de baja correctamente',
            });
            setDeleteModalVisible(false);
            setPromoterToDelete(null);
            setDeleteConfirmText('');
            setDeletionReason('');
            fetchPromoters(filters, pagination.current, pagination.pageSize).catch(console.error);
        } catch (error) {
            modal.error({
                title: 'Error',
                content: error.message || 'Error al dar de baja el promotor',
            });
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleEditEmailClick = (record, e) => {
        e.stopPropagation();
        setPromoterToEditEmail(record);
        emailForm.resetFields();
        setEmailModalVisible(true);
    };

    const handleEmailUpdate = async (values) => {
        setEmailLoading(true);
        try {
            await promoterService.updateEmail(promoterToEditEmail.id, values.email);
            modal.success({
                title: 'Éxito',
                content: 'Correo actualizado correctamente. Se ha enviado un nuevo correo de bienvenida.',
            });
            setEmailModalVisible(false);
            setPromoterToEditEmail(null);
            emailForm.resetFields();
            fetchPromoters(filters, pagination.current, pagination.pageSize).catch(console.error);
        } catch (error) {
            modal.error({
                title: 'Error',
                content: error.message || 'Error al actualizar el correo',
            });
        } finally {
            setEmailLoading(false);
        }
    };

    return (
        <>
            {contextHolder}
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                {(userRole === SECTIONAL_ROLE || userRole === DISTRICT_ROLE || userRole === COT_ROLE) && (
                    <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "20px"}} hidden={true}>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined/>} 
                            onClick={handleNewPromoter}
                            size="large"
                        >
                            Nuevo Promotor
                        </Button>
                    </div>
                )}
                
                <Title level={2} style={{textAlign: "center"}}>
                    Lista de promotores
                </Title>

                <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                    <Col xs={24} sm={24} md={24}>
                        <Select
                            mode="multiple"
                            style={{width: '100%'}}
                            placeholder="Filtrar por Sección"
                            value={tempFilters.sectionId}
                            onChange={(value) => handleFilterChange("sectionId", value)}
                            options={Array.from(new Set(userSections)).map(sec => ({
                                label: `Sección ${sec}`,
                                value: sec
                            }))}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={24}>
                        <Space style={{float: "right"}}>
                            <Button type="primary" icon={<SearchOutlined/>} onClick={applyFilters}>
                                Aplicar Filtros
                            </Button>
                            <Button icon={<UndoOutlined/>} onClick={resetFilters}>Restablecer</Button>
                        </Space>
                    </Col>
                </Row>

                {(userRole === SECTIONAL_ROLE || userRole === DISTRICT_ROLE || userRole === COT_ROLE) && (
                    <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "20px"}}>
                        <Button
                            type="default"
                            onClick={() => setShowRejectedModal(true)}
                        >
                            Visualizar promotores rechazados
                        </Button>
                    </div>
                )}

                <Table
                    columns={isMobile ? mobileColumns : desktopColumns}
                    dataSource={promoters}
                    rowKey="electorKey"
                    loading={loading}
                    scroll={{x: "max-content"}}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50"],
                    }}
                    onChange={handleTableChange}
                    onRow={(record) => ({
                        onClick: () => {
                            handleRowClick(record)
                        },
                        style: {cursor: "pointer"},
                    })}
                />
            </motion.div>

            <Modal
                title="Detalles del promotor"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                {selectedPromoter && (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item
                            label="Nombre completo">{selectedPromoter.fullName || selectedPromoter.name + " " + selectedPromoter.firstLastName + " " + selectedPromoter.secondLastName}</Descriptions.Item>
                        <Descriptions.Item label="Clave de elector">{selectedPromoter.electorKey}</Descriptions.Item>
                        <Descriptions.Item label="Teléfono">{selectedPromoter.phone}</Descriptions.Item>
                        <Descriptions.Item label="E-mail">{selectedPromoter.email}</Descriptions.Item>
                        <Descriptions.Item label="Dirección">
                            {[
                                selectedPromoter.street && `${selectedPromoter.street} ${selectedPromoter.exteriorNumber || ''}${selectedPromoter.interiorNumber ? ` Int. ${selectedPromoter.interiorNumber}` : ''}`.trim(),
                                selectedPromoter.neighborhood,
                                selectedPromoter.zipCode && `C.P. ${selectedPromoter.zipCode}`,
                            ].filter(Boolean).join(', ') || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Entidad">{selectedPromoter.entityId}</Descriptions.Item>
                        <Descriptions.Item
                            label="Sección">{selectedPromoter.sections ? selectedPromoter.sections.join(", ") : selectedPromoter.sectionId}</Descriptions.Item>
                        <Descriptions.Item
                            label="Roles">{selectedPromoter.roles ? selectedPromoter.roles.join(", ") : PROMOTER_ROLE}</Descriptions.Item>
                        <Descriptions.Item label="Total actual">{selectedPromoter.count}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            <Modal
                open={isFormModalVisible}
                onCancel={() => setIsFormModalVisible(false)}
                footer={null}
                width={isMobile ? "90%" : "45%"}
            >
                <PromoterRegisterForm
                    selectedSection={selectedSection}
                    onSuccess={() => {
                        setIsFormModalVisible(false);
                        fetchPromoters(filters).catch();
                    }}
                />
            </Modal>

            <RejectedPromotersModal
                visible={showRejectedModal}
                onClose={() => setShowRejectedModal(false)}
            />

            <Modal
                title="Confirmar baja de promotor"
                open={deleteModalVisible}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setPromoterToDelete(null);
                    setDeleteConfirmText('');
                    setDeletionReason('');
                }}
                onOk={handleDeleteConfirm}
                okText="Confirmar baja"
                cancelText="No, mantener el registro"
                okButtonProps={{ danger: true, loading: deleteLoading }}
            >
                {promoterToDelete && (
                    <div>
                        <p>¿Está seguro que desea dar de baja al promotor?</p>
                        <p><strong>{promoterToDelete.name} {promoterToDelete.firstLastName} {promoterToDelete.secondLastName}</strong></p>
                        <p style={{marginTop: '16px'}}>Motivo de baja *:</p>
                        <Input.TextArea
                            placeholder="Ingrese el motivo de baja"
                            value={deletionReason}
                            onChange={(e) => setDeletionReason(e.target.value)}
                            rows={3}
                            style={{marginBottom: '16px'}}
                            required
                        />
                        <p>Para confirmar, ingrese la clave de elector: <strong>({promoterToDelete.electorKey})</strong></p>
                        <Input
                            placeholder="Ingrese la clave de elector"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                            maxLength={18}
                        />
                    </div>
                )}
            </Modal>

            <Modal
                title="Editar correo electrónico"
                open={emailModalVisible}
                onCancel={() => {
                    setEmailModalVisible(false);
                    setPromoterToEditEmail(null);
                    emailForm.resetFields();
                }}
                footer={null}
            >
                {promoterToEditEmail && (
                    <div>
                        <p>Promotor: <strong>{promoterToEditEmail.name} {promoterToEditEmail.firstLastName} {promoterToEditEmail.secondLastName}</strong></p>
                        <p>Correo actual: <strong>{promoterToEditEmail.email}</strong></p>
                        <Form
                            form={emailForm}
                            onFinish={handleEmailUpdate}
                            layout="vertical"
                            style={{marginTop: '20px'}}
                        >
                            <Form.Item
                                name="email"
                                label="Nuevo correo electrónico"
                                rules={[
                                    {required: true, message: 'El correo es obligatorio'},
                                    {type: 'email', message: 'Ingrese un correo válido'},
                                    temporaryEmailRule,
                                ]}
                            >
                                <Input
                                    placeholder="correo@ejemplo.com"
                                    onPaste={(e) => e.preventDefault()}
                                />
                            </Form.Item>
                            <Form.Item
                                name="emailConfirm"
                                label="Confirmar correo electrónico"
                                dependencies={['email']}
                                rules={[
                                    {required: true, message: 'Confirme el correo'},
                                    ({getFieldValue}) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('email') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Los correos no coinciden'));
                                        },
                                    }),
                                ]}
                            >
                                <Input
                                    placeholder="correo@ejemplo.com"
                                    onPaste={(e) => e.preventDefault()}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Space style={{float: 'right'}}>
                                    <Button onClick={() => {
                                        setEmailModalVisible(false);
                                        setPromoterToEditEmail(null);
                                        emailForm.resetFields();
                                    }}>
                                        Cancelar
                                    </Button>
                                    <Button type="primary" htmlType="submit" loading={emailLoading}>
                                        Actualizar correo
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default PromotersPage;